'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import { parseApiAsset, ZERO } from '@morpho-org/dapp-core';
import { CompactAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useMemo, useState } from 'react';
import type { MarketLendWithdrawSource } from '@/lib/analytics/client/events';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { DISABLED_LEND_TOOLTIP } from '@/components/common/DisabledActionTooltip';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { TransferError } from '@/components/common/Error/TransferErrors';
import { MarketLendSummaryCard } from '@/components/common/MarketLendSummary';
import { POSITION_MANAGER_DIALOG_OPTIONS } from '@/components/common/PositionManager/dialogOptions';
import { WrapSegment } from '@/components/common/WrapSegment';
import MarketLendWithdrawReviewDialogV2 from '@/components/MarketPage/MarketActionV2/MarketLendWithdrawReviewDialogV2';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getMarketGeoRestrictionActionProps } from '@/helpers/marketGeoRestriction';
import useMarketTooltip from '@/hooks/operation/market/useMarketTooltip';
import { useMarketLendForm } from '@/hooks/operation/market/v2/useMarketLendForm';
import usePositionManagerMarketDialogLabels from '@/hooks/operation/position-manager/usePositionManagerMarketDialogLabels';
import useBundleCanTransferOnchain from '@/hooks/operation/useBundleCanTransferOnchain';
import { refetchLendPositionsUntilIndexed } from '@/hooks/positions/refetchLendPositionsUntilIndexed';
import { useMarketGeoRestriction } from '@/hooks/useMarketGeoRestriction';
import { useTrackMarketActionsV2EntryViewed } from '@/lib/analytics/client/hooks/useTrackMarketActionsV2EntryViewed';
import { trackMarketActionsV2EntryClicked } from '@/lib/analytics/client/trackers/market-actions-v2';
import { buildMarketLendCompletedParams, trackMarketLendCompleted } from '@/lib/analytics/client/trackers/market-lend';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
import PositionManagerDialogLoading from '../PositionManagerDialogLoading';
import { useLendWithdrawDialogData } from '../useMarketDialogData';
interface PositionManagerLendDialogContentProps {
  marketId: MarketId;
  chainId: number;
  source: MarketLendWithdrawSource;
}
function isAssetInputLoading(isMarketLendEnabled: boolean, isConnected: boolean, isPositionLoading: boolean, isMaxLoading: boolean) {
  return isMarketLendEnabled && isConnected && (isPositionLoading || isMaxLoading);
}

/**
 * Position Manager supply flow for a direct market lend. It shares the withdraw dialog's market
 * query, so the second of the two to open reads it warm.
 */
function PositionManagerLendDialogContent(t0) {
  const $ = _c(109);
  const {
    marketId,
    chainId,
    source
  } = t0;
  const {
    account,
    targetChain
  } = useWeb3Context();
  const {
    toggleDialog
  } = useDialogContext();
  const queryClient = useQueryClient();
  const [isInputPending, setIsInputPending] = useState(false);
  let t1;
  if ($[0] !== chainId || $[1] !== marketId) {
    t1 = {
      chainId,
      marketId
    };
    $[0] = chainId;
    $[1] = marketId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: dialogData,
    isPending: queryIsLoading,
    error: queryError
  } = useLendWithdrawDialogData(t1);
  dialogData?.loanAsset;
  const t2 = dialogData?.loanAsset;
  let t3;
  if ($[3] !== t2) {
    t3 = parseApiAsset(t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const loanAsset = t3;
  let t4;
  if ($[5] !== dialogData?.collateralAsset) {
    t4 = () => parseApiAsset(dialogData?.collateralAsset);
    $[5] = dialogData?.collateralAsset;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  dialogData?.collateralAsset;
  let t5;
  if ($[7] !== t4) {
    t5 = t4();
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const collateralAsset = t5;
  const marketParams = dialogData?.marketParams;
  let t6;
  if ($[9] !== loanAsset || $[10] !== marketParams) {
    t6 = {
      marketParams,
      loanAsset
    };
    $[9] = loanAsset;
    $[10] = marketParams;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    isMarketLendEnabled,
    inputLendAmount,
    setInputLendAmountManually,
    positionData,
    positionError,
    isPositionLoading,
    currentSupply,
    afterSupply,
    maxLend,
    isMaxActive,
    isMaxLoading,
    onMaxClick,
    isWNativeLoan,
    isWrapping,
    onWrappingChange,
    wrappableNativeBalance,
    nativeAmount,
    isWrappingToggleLoading,
    buildErrorMessage,
    canTransferLoanAsset,
    isBlockedByBorrowPosition,
    isDebouncing,
    isFinalizeDisabled,
    isButtonLoading,
    reset
  } = useMarketLendForm(t6);
  const {
    canTransfer: bundleCanTransfer
  } = useBundleCanTransferOnchain("bundles.blueBundlesV1", loanAsset?.address, targetChain.id);
  const t7 = dialogData?.collateralAsset?.address;
  const t8 = dialogData?.loanAsset.address;
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = [t7, t8];
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== chainId || $[16] !== t9) {
    t10 = {
      chainId,
      assetAddresses: t9
    };
    $[15] = chainId;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const geoRestriction = useMarketGeoRestriction(t10);
  let t11;
  if ($[18] !== isMarketLendEnabled || $[19] !== marketId || $[20] !== source || $[21] !== targetChain.id || $[22] !== targetChain.name) {
    t11 = {
      entryPoint: "position_manager",
      action: "lend",
      marketId,
      chainId: targetChain.id,
      chainName: targetChain.name,
      source,
      enabled: isMarketLendEnabled
    };
    $[18] = isMarketLendEnabled;
    $[19] = marketId;
    $[20] = source;
    $[21] = targetChain.id;
    $[22] = targetChain.name;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  useTrackMarketActionsV2EntryViewed(t11);
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [];
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== buildErrorMessage || $[26] !== inputLendAmount) {
    t13 = {
      operationLabel: "lend",
      inputCollateralAmount: ZERO,
      inputLoanAmount: inputLendAmount,
      isNew: true,
      localErrorMessage: buildErrorMessage,
      operations: t12,
      simulationError: undefined,
      blockError: null,
      isPending: false
    };
    $[25] = buildErrorMessage;
    $[26] = inputLendAmount;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const {
    confirmButtonLabel
  } = usePositionManagerMarketDialogLabels(t13);
  const tooltipContent = useMarketTooltip(isBlockedByBorrowPosition ? DISABLED_LEND_TOOLTIP : buildErrorMessage);
  const t14 = isFinalizeDisabled || !bundleCanTransfer;
  let t15;
  if ($[28] !== confirmButtonLabel || $[29] !== geoRestriction || $[30] !== isButtonLoading || $[31] !== t14 || $[32] !== tooltipContent) {
    t15 = getMarketGeoRestrictionActionProps({
      geoRestriction,
      isFinalizeDisabled: t14,
      isLoading: isButtonLoading,
      finalizeLabel: confirmButtonLabel,
      tooltipContent
    });
    $[28] = confirmButtonLabel;
    $[29] = geoRestriction;
    $[30] = isButtonLoading;
    $[31] = t14;
    $[32] = tooltipContent;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let geoActionProps;
  let shouldBlockFinalize;
  if ($[34] !== t15) {
    ({
      shouldBlockFinalize,
      ...geoActionProps
    } = t15);
    $[34] = t15;
    $[35] = geoActionProps;
    $[36] = shouldBlockFinalize;
  } else {
    geoActionProps = $[35];
    shouldBlockFinalize = $[36];
  }
  let t16;
  if ($[37] !== account.address || $[38] !== account.isContract || $[39] !== account.isSafe || $[40] !== chainId || $[41] !== collateralAsset || $[42] !== dialogData?.avgApy || $[43] !== dialogData?.marketForRewards || $[44] !== inputLendAmount || $[45] !== loanAsset || $[46] !== marketId || $[47] !== marketParams || $[48] !== nativeAmount || $[49] !== queryClient || $[50] !== reset || $[51] !== shouldBlockFinalize || $[52] !== source || $[53] !== targetChain.id || $[54] !== targetChain.name || $[55] !== toggleDialog) {
    t16 = () => {
      if (!marketParams || !loanAsset || shouldBlockFinalize) {
        return;
      }
      trackMarketActionsV2EntryClicked({
        entryPoint: "position_manager",
        action: "lend",
        marketId,
        chainId: targetChain.id,
        chainName: targetChain.name,
        source
      });
      toggleDialog(<MarketLendWithdrawReviewDialogV2 marketParams={marketParams} mode="lend" amount={inputLendAmount} nativeAmount={nativeAmount} collateralAsset={collateralAsset} loanAsset={loanAsset} marketForRewards={dialogData?.marketForRewards} avgSupplyApy={dialogData?.avgApy} onTxSuccess={lendAmount => {
        const params = buildMarketLendCompletedParams({
          marketId,
          loanAsset,
          lendAmount,
          chainId: targetChain.id,
          chainName: targetChain.name,
          walletType: getWalletType(account.isSafe, account.isContract),
          entryPoint: "position_manager",
          source
        });
        if (params) {
          trackMarketLendCompleted(params);
        }
        queryClient.invalidateQueries();
        refetchLendPositionsUntilIndexed(queryClient, account.address, {
          chainId,
          marketId
        });
        reset();
      }} entryPoint="position_manager" />, POSITION_MANAGER_DIALOG_OPTIONS);
    };
    $[37] = account.address;
    $[38] = account.isContract;
    $[39] = account.isSafe;
    $[40] = chainId;
    $[41] = collateralAsset;
    $[42] = dialogData?.avgApy;
    $[43] = dialogData?.marketForRewards;
    $[44] = inputLendAmount;
    $[45] = loanAsset;
    $[46] = marketId;
    $[47] = marketParams;
    $[48] = nativeAmount;
    $[49] = queryClient;
    $[50] = reset;
    $[51] = shouldBlockFinalize;
    $[52] = source;
    $[53] = targetChain.id;
    $[54] = targetChain.name;
    $[55] = toggleDialog;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  const handleFinalize = t16;
  if (queryIsLoading) {
    let t17;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <PositionManagerDialogLoading />;
      $[57] = t17;
    } else {
      t17 = $[57];
    }
    return t17;
  }
  if (queryError || positionError && !positionData || !loanAsset) {
    let t17;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <ErrorServiceUnavailable minHeight="360px" />;
      $[58] = t17;
    } else {
      t17 = $[58];
    }
    return t17;
  }
  const t17 = `Lend ${loanAsset.symbol}`;
  let t18;
  if ($[59] !== account.isConnected || $[60] !== isMarketLendEnabled || $[61] !== isMaxLoading || $[62] !== isPositionLoading) {
    t18 = isAssetInputLoading(isMarketLendEnabled, account.isConnected, isPositionLoading, isMaxLoading);
    $[59] = account.isConnected;
    $[60] = isMarketLendEnabled;
    $[61] = isMaxLoading;
    $[62] = isPositionLoading;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  let t19;
  if ($[64] !== inputLendAmount || $[65] !== isMaxActive || $[66] !== loanAsset || $[67] !== maxLend || $[68] !== onMaxClick || $[69] !== setInputLendAmountManually || $[70] !== t17 || $[71] !== t18) {
    t19 = <CompactAssetInput header={t17} amount={inputLendAmount} token={loanAsset} onChange={setInputLendAmountManually} onPendingChange={setIsInputPending} maxValue={maxLend} onMaxClick={onMaxClick} isMax={isMaxActive} loading={t18} autoFocus={true} />;
    $[64] = inputLendAmount;
    $[65] = isMaxActive;
    $[66] = loanAsset;
    $[67] = maxLend;
    $[68] = onMaxClick;
    $[69] = setInputLendAmountManually;
    $[70] = t17;
    $[71] = t18;
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  let t20;
  if ($[73] !== isMarketLendEnabled || $[74] !== isWNativeLoan || $[75] !== isWrapping || $[76] !== isWrappingToggleLoading || $[77] !== loanAsset.symbol || $[78] !== onWrappingChange || $[79] !== targetChain.nativeCurrency.symbol || $[80] !== wrappableNativeBalance) {
    t20 = <WrapSegment id="position-manager-lend" enabled={isMarketLendEnabled} isWNative={isWNativeLoan} wrappableNativeBalance={wrappableNativeBalance} wrappedSymbol={loanAsset.symbol} nativeSymbol={targetChain.nativeCurrency.symbol} isWrapping={isWrapping} onWrappingChange={onWrappingChange} disabled={isWrappingToggleLoading} />;
    $[73] = isMarketLendEnabled;
    $[74] = isWNativeLoan;
    $[75] = isWrapping;
    $[76] = isWrappingToggleLoading;
    $[77] = loanAsset.symbol;
    $[78] = onWrappingChange;
    $[79] = targetChain.nativeCurrency.symbol;
    $[80] = wrappableNativeBalance;
    $[81] = t20;
  } else {
    t20 = $[81];
  }
  let t21;
  if ($[82] !== canTransferLoanAsset || $[83] !== loanAsset.address) {
    t21 = !canTransferLoanAsset && <TransferError tokenAddress={loanAsset.address} />;
    $[82] = canTransferLoanAsset;
    $[83] = loanAsset.address;
    $[84] = t21;
  } else {
    t21 = $[84];
  }
  let t22;
  if ($[85] !== t19 || $[86] !== t20 || $[87] !== t21) {
    t22 = <div className="flex flex-col gap-[2px]">{t19}{t20}{t21}</div>;
    $[85] = t19;
    $[86] = t20;
    $[87] = t21;
    $[88] = t22;
  } else {
    t22 = $[88];
  }
  const t23 = dialogData?.marketForRewards;
  const t24 = dialogData?.avgApy;
  let t25;
  if ($[89] !== afterSupply || $[90] !== currentSupply || $[91] !== isPositionLoading || $[92] !== loanAsset || $[93] !== marketId || $[94] !== t23 || $[95] !== t24) {
    t25 = <MarketLendSummaryCard marketId={marketId} currentSupply={currentSupply} afterSupply={afterSupply} loanAsset={loanAsset} isLoading={isPositionLoading} marketForRewards={t23} avgSupplyApy={t24} background="muted" />;
    $[89] = afterSupply;
    $[90] = currentSupply;
    $[91] = isPositionLoading;
    $[92] = loanAsset;
    $[93] = marketId;
    $[94] = t23;
    $[95] = t24;
    $[96] = t25;
  } else {
    t25 = $[96];
  }
  let t26;
  if ($[97] !== bundleCanTransfer) {
    t26 = !bundleCanTransfer && <BundleAuthorizationCallout variant="destructive" message={MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[97] = bundleCanTransfer;
    $[98] = t26;
  } else {
    t26 = $[98];
  }
  let t27;
  if ($[99] !== geoActionProps || $[100] !== handleFinalize || $[101] !== isDebouncing || $[102] !== isInputPending) {
    t27 = <ActionButtons handleFinalize={handleFinalize} {...geoActionProps} isInputPending={isInputPending} isValidating={isDebouncing} />;
    $[99] = geoActionProps;
    $[100] = handleFinalize;
    $[101] = isDebouncing;
    $[102] = isInputPending;
    $[103] = t27;
  } else {
    t27 = $[103];
  }
  let t28;
  if ($[104] !== t22 || $[105] !== t25 || $[106] !== t26 || $[107] !== t27) {
    t28 = <>{t22}{t25}{t26}{t27}</>;
    $[104] = t22;
    $[105] = t25;
    $[106] = t26;
    $[107] = t27;
    $[108] = t28;
  } else {
    t28 = $[108];
  }
  return t28;
}
export default memo(PositionManagerLendDialogContent);